import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCalendar3, BsPerson, BsBuilding, BsBriefcase, BsArrowRight, BsPencil, BsTrash, BsX } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Textarea from '#globalComponents/Forms/Textarea';

function formatTime(d) {
	return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDayLabel(d) {
	const now = new Date();
	const diff = Math.floor(
		(new Date(d.getFullYear(), d.getMonth(), d.getDate()) -
			new Date(now.getFullYear(), now.getMonth(), now.getDate())) /
			86400000,
	);
	if (diff === -1) return 'Gestern';
	if (diff === 0) return 'Heute';
	if (diff === 1) return 'Morgen';
	if (diff > 1 && diff < 7) return d.toLocaleDateString('de-DE', { weekday: 'long' });
	return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'short' });
}

const STATUS_LABELS = {
	completed: 'Erledigt',
	planned: 'Geplant',
	in_progress: 'Wird versendet',
	failed: 'Fehlgeschlagen',
	cancelled: 'Abgebrochen',
};

function getStatusLabel(status) {
	return STATUS_LABELS[status] ?? status;
}

const OwnerField = ({ activity, members, isReadOnly, onReassign }) => {
	if (isReadOnly) {
		return (
			<span className="text-xs font-medium text-text">
				{members.find((m) => m.userId === activity.userId)?.fullName ?? '\u2013'}
			</span>
		);
	}
	return (
		<select
			value={activity.userId ?? ''}
			onChange={(e) => onReassign(activity.id, e.target.value)}
			className="text-xs font-medium text-text bg-transparent border-b border-transparent hover:border-bgBase-600 outline-none cursor-pointer py-0 px-0"
		>
			<option value="">Nicht zugewiesen</option>
			{members.map((m) => (
				<option key={m.userId} value={m.userId}>
					{m.fullName ?? m.email}
				</option>
			))}
		</select>
	);
};

OwnerField.propTypes = {
	activity: PropTypes.object.isRequired,
	members: PropTypes.array.isRequired,
	isReadOnly: PropTypes.bool.isRequired,
	onReassign: PropTypes.func.isRequired,
};

const ActivityDrawerContent = ({
	activity,
	members,
	dueDate,
	isOverdue,
	isAutomated,
	isMeetingOrCalendar,
	isReadOnly,
	onReassign,
	onUpdate,
	onDelete,
	onCancelAutomated,
	navigate,
}) => {
	const [editingNotes, setEditingNotes] = useState(false);
	const [notesDraft, setNotesDraft] = useState(activity.body ?? '');

	return (
		<>
			<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
				<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800 mb-2.5">Details</h4>
				<div className="flex flex-col gap-2">
					{dueDate && (
						<div className="flex justify-between text-xs">
							<span className="text-bgBase-800 flex items-center gap-1.5">
								<BsCalendar3 className="w-3 h-3" /> Fällig
							</span>
							<span className={`font-medium ${isOverdue ? 'text-warning-500' : 'text-text'}`}>
								{formatDayLabel(dueDate)}, {formatTime(dueDate)}
							</span>
						</div>
					)}
					{!isMeetingOrCalendar && !isAutomated && (
						<div className="flex justify-between items-center text-xs">
							<span className="text-bgBase-800 flex items-center gap-1.5">
								<BsPerson className="w-3 h-3" /> Besitzer
							</span>
							<OwnerField
								activity={activity}
								members={members}
								isReadOnly={isReadOnly}
								onReassign={onReassign}
							/>
						</div>
					)}
					{activity.status && (
						<div className="flex justify-between text-xs">
							<span className="text-bgBase-800">Status</span>
							<span
								className={`font-medium ${activity.status === 'failed' ? 'text-warning-500' : 'text-text'}`}
							>
								{getStatusLabel(activity.status)}
							</span>
						</div>
					)}
					{activity.status === 'failed' && activity.channelPayload?.errorMessage && (
						<div className="flex justify-between text-xs">
							<span className="text-bgBase-800">Fehler</span>
							<span className="font-medium text-warning-500 text-right max-w-[200px]">
								{activity.channelPayload.errorMessage}
							</span>
						</div>
					)}
				</div>
			</div>

			{(activity.crmCompanyId || activity.dealId) && (
				<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
					<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800 mb-2.5">Verknüpft</h4>
					<div className="flex flex-col gap-2">
						{activity.crmCompanyId && (
							<button
								type="button"
								onClick={() => navigate(`/crm/companies/${activity.crmCompanyId}`)}
								className="flex items-center gap-2 text-xs text-primary-600 hover:text-primary-700 transition-colors"
							>
								<BsBuilding className="w-3 h-3" /> {activity.companyName}{' '}
								<BsArrowRight className="w-2.5 h-2.5 ml-auto" />
							</button>
						)}
						{activity.dealId && (
							<div className="flex items-center gap-2 text-xs text-bgBase-800">
								<BsBriefcase className="w-3 h-3" /> Deal verknüpft
							</div>
						)}
					</div>
				</div>
			)}

			<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
				<div className="flex items-center justify-between mb-2.5">
					<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800">Notizen</h4>
					{!editingNotes && !isReadOnly && (
						<button
							type="button"
							onClick={() => {
								setNotesDraft(activity.body ?? '');
								setEditingNotes(true);
							}}
							className="inline-flex items-center gap-1 text-[11px] text-bgBase-800 hover:text-text transition-colors"
						>
							<BsPencil className="w-2.5 h-2.5" /> Bearbeiten
						</button>
					)}
				</div>
				{editingNotes ? (
					<div className="flex flex-col gap-2">
						<Textarea
							value={notesDraft}
							onChange={(e) => setNotesDraft(e.target.value)}
							rows={4}
							placeholder="Notizen, Vorbereitung, Outcomes ..."
						/>
						<div className="flex gap-1.5 justify-end">
							<Button type="button" variant="outline" size="sm" onClick={() => setEditingNotes(false)}>
								Abbrechen
							</Button>
							<Button
								type="button"
								variant="solid"
								size="sm"
								onClick={() => {
									onUpdate(activity.id, { body: notesDraft });
									setEditingNotes(false);
								}}
							>
								Speichern
							</Button>
						</div>
					</div>
				) : (
					<p className={`text-xs leading-relaxed ${activity.body ? 'text-text' : 'text-bgBase-800 italic'}`}>
						{activity.body || 'Keine Notizen.'}
					</p>
				)}
			</div>

			<div className="px-5 pb-5 border-t border-bgBase-400 pt-4">
				{isAutomated && activity.status !== 'completed' ? (
					<button
						type="button"
						onClick={() => onCancelAutomated(activity)}
						className="text-[11px] text-warning-500 hover:text-warning-700 flex items-center gap-1 transition-colors"
					>
						<BsX className="w-3 h-3" /> Aktivität abbrechen
					</button>
				) : (
					!isReadOnly && (
						<button
							type="button"
							onClick={() => onDelete(activity.id)}
							className="text-[11px] text-warning-500 hover:text-warning-700 flex items-center gap-1 transition-colors"
						>
							<BsTrash className="w-2.5 h-2.5" /> Aktivität löschen
						</button>
					)
				)}
			</div>
		</>
	);
};

ActivityDrawerContent.propTypes = {
	activity: PropTypes.object.isRequired,
	members: PropTypes.array.isRequired,
	dueDate: PropTypes.instanceOf(Date),
	isOverdue: PropTypes.bool.isRequired,
	isAutomated: PropTypes.bool.isRequired,
	isMeetingOrCalendar: PropTypes.bool.isRequired,
	isReadOnly: PropTypes.bool.isRequired,
	onReassign: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onCancelAutomated: PropTypes.func,
	navigate: PropTypes.func.isRequired,
};

export default ActivityDrawerContent;
